import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Chip, Tooltip, Menu, MenuItem,
  Accordion, AccordionSummary, AccordionDetails,
  Select, FormControl, InputLabel, InputAdornment,
  CircularProgress, Alert, ToggleButtonGroup, ToggleButton, Divider,
  Link, Stack, useMediaQuery, useTheme, Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import LinkIcon from '@mui/icons-material/Link';
import FilterListIcon from '@mui/icons-material/FilterList';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import {
  getPatients, createPatient, updatePatient, deletePatient,
  getComments, createComment, exportPatients, searchPatientsByDni, getDoctors,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useApiCall } from '../hooks/useApiCall';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import PatientAvatar from '../components/PatientAvatar';
import SelfUpdateLinkDialog from '../components/SelfUpdateLinkDialog';
import { useNewAppointmentFlow } from '../components/NewAppointmentFlow';

// Un paciente puede tener varias coberturas — se muestra la principal en
// la lista (o el campo único legado si todavía no migró a la lista nueva).
const primaryInsuranceOf = (p) => {
  const list = p.insurances || [];
  if (list.length === 0) {
    return p.insurance_provider ? { provider: p.insurance_provider, policy_number: p.insurance_policy_number } : null;
  }
  return list.find(i => i.is_primary) || list[0];
};

// Origen de la ficha del paciente (Patient.created_via) — pedido explícito
// del usuario 2026-10-20: distinguir alta manual (staff la carga directo),
// por link (el propio paciente completa sus datos sin supervisión) o
// desde turno (alta rápida al agendar, mismo formulario simplificado).
// Se muestra como ícono junto al nombre (no columna aparte) — pedido
// explícito del usuario 2026-10-20. Manual no lleva ícono: es el caso
// mayoritario y no aporta distinguirlo visualmente en cada fila.
const CREATED_VIA_CONFIG = {
  manual: { label: 'Manual', color: 'default' },
  link: { label: 'Link', color: 'info', Icon: LinkIcon },
  turno: { label: 'Turno', color: 'success', Icon: EventAvailableIcon },
};

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GENDERS = [
  { value: 'masculino', label: 'Masculino' },
  { value: 'femenino', label: 'Femenino' },
  { value: 'otro', label: 'Otro' },
];

const emptyPatient = {
  first_name: '', last_name: '', phone: '', email: '', address: '',
  dob: '', dni: '', gender: '', marital_status: '',
  blood_type: '', allergies: '', chronic_diseases: '', current_medications: '',
  insurance_provider: '', insurance_policy_number: '',
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  permanent_notes: '', is_active: true,
};

// ── Header de página consistente ─────────────────────────────────────────

const PageHeader = ({ title, subtitle, action }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
    <Box>
      <Typography variant="h4">{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>}
    </Box>
    {action}
  </Box>
);

// ── Formulario de paciente ────────────────────────────────────────────────

const PatientForm = ({ patientData, setPatientData, error, fieldErrors = {}, isEdit = false }) => {
  const set = (field) => (e) => setPatientData(prev => ({ ...prev, [field]: e.target.value }));
  const [dniWarning, setDniWarning] = useState(null); // { id, name } del existente
  const [dniChecking, setDniChecking] = useState(false);

  const checkDni = async (dni) => {
    if (isEdit || !dni || !dni.trim()) { setDniWarning(null); return; }
    setDniChecking(true);
    try {
      const res = await searchPatientsByDni(dni.trim());
      const found = (res.data?.items || []).find(p => p.dni === dni.trim() && p.is_active);
      setDniWarning(found ? { id: found.id, name: found.name } : null);
    } catch { setDniWarning(null); }
    finally { setDniChecking(false); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {error && <Alert severity="error" sx={{ mb: 0.5 }}>{error}</Alert>}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Datos básicos</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="Apellido *" fullWidth required value={patientData.last_name} onChange={set('last_name')}
              error={!!fieldErrors.last_name} helperText={fieldErrors.last_name} />
            <TextField label="Nombre *" fullWidth required value={patientData.first_name} onChange={set('first_name')}
              error={!!fieldErrors.first_name} helperText={fieldErrors.first_name} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              label="DNI / Documento" fullWidth value={patientData.dni}
              onChange={set('dni')}
              onBlur={(e) => checkDni(e.target.value)}
              error={!!fieldErrors.dni} helperText={fieldErrors.dni}
              InputProps={{ endAdornment: dniChecking ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment> : null }}
            />
            <FormControl fullWidth error={!!fieldErrors.gender}>
              <InputLabel>Sexo</InputLabel>
              <Select value={patientData.gender} label="Sexo" onChange={set('gender')}>
                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                {GENDERS.map(g => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {dniWarning && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Ya existe un paciente activo con este DNI:{' '}
              <Link component={RouterLink} to={`/patients/${dniWarning.id}`} fontWeight={700}>
                {dniWarning.name}
              </Link>
            </Alert>
          )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="Fecha de nacimiento" type="date" fullWidth InputLabelProps={{ shrink: true }} value={patientData.dob} onChange={set('dob')}
              error={!!fieldErrors.dob} helperText={fieldErrors.dob} />
            <TextField label="Estado civil" fullWidth value={patientData.marital_status} onChange={set('marital_status')} />
          </Box>
          <TextField label="Teléfono" fullWidth value={patientData.phone} onChange={set('phone')}
            error={!!fieldErrors.phone} helperText={fieldErrors.phone} />
          <TextField label="Email" fullWidth value={patientData.email} onChange={set('email')}
            error={!!fieldErrors.email} helperText={fieldErrors.email} />
          <TextField label="Dirección" fullWidth value={patientData.address} onChange={set('address')} />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Información médica</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FormControl fullWidth>
            <InputLabel>Grupo sanguíneo</InputLabel>
            <Select value={patientData.blood_type} label="Grupo sanguíneo" onChange={set('blood_type')}>
              <MenuItem value=""><em>Desconocido</em></MenuItem>
              {BLOOD_TYPES.map(bt => <MenuItem key={bt} value={bt}>{bt}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Alergias" fullWidth multiline rows={2} value={patientData.allergies} onChange={set('allergies')} />
          <TextField
            label="Antecedentes (texto libre, legado)"
            fullWidth multiline rows={2}
            value={patientData.chronic_diseases}
            onChange={set('chronic_diseases')}
            helperText='Para condiciones crónicas activas, usá "Problemas" en la Historia Clínica del paciente una vez creado.'
          />
          <TextField label="Medicación actual" fullWidth multiline rows={2} value={patientData.current_medications} onChange={set('current_medications')} />
          <TextField label="Notas permanentes" fullWidth multiline rows={2} value={patientData.permanent_notes} onChange={set('permanent_notes')} />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Obra social / Cobertura</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField label="Obra social / Prepaga" fullWidth value={patientData.insurance_provider} onChange={set('insurance_provider')} />
          <TextField label="Número de afiliado" fullWidth value={patientData.insurance_policy_number} onChange={set('insurance_policy_number')} />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Contacto de emergencia</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField label="Nombre" fullWidth value={patientData.emergency_contact_name} onChange={set('emergency_contact_name')} />
          <TextField label="Teléfono" fullWidth value={patientData.emergency_contact_phone} onChange={set('emergency_contact_phone')} />
          <TextField label="Relación (ej: cónyuge)" fullWidth value={patientData.emergency_contact_relationship} onChange={set('emergency_contact_relationship')} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

// ── Página principal ──────────────────────────────────────────────────────

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const Patients = () => {
  const { isAdmin, isRecepcionista, isMedico } = useAuth();
  // El médico también puede crear/editar/dar de baja fichas de pacientes —
  // en clínicas chicas recepción no siempre está disponible (pedido
  // explícito del usuario). El backend ya lo permite sin restringir a
  // "sus" pacientes, porque un paciente no pertenece a un médico específico.
  const canEdit = isAdmin || isRecepcionista || isMedico;
  const navigate = useNavigate();
  const apiCall = useApiCall();
  const theme = useTheme();
  const { features } = useClinicFeatures();
  const [selfUpdateDialog, setSelfUpdateDialog] = useState(null); // null | { patientId, patientPhone }
  // "Nuevo turno" desde el menú de fila — pedido explícito del usuario,
  // mismo flujo ya usado en el perfil del paciente (ver
  // NewAppointmentFlow.js). Acá no había lista de médicos cargada todavía.
  const [doctorsArr, setDoctorsArr] = useState([]);
  const [newApptPatient, setNewApptPatient] = useState(null); // paciente elegido desde el menú de fila
  useEffect(() => {
    getDoctors({ limit: 500 }).then(res => setDoctorsArr(res.data?.items || [])).catch(() => {});
  }, []);
  // La tabla de 7 columnas no entra en pantallas chicas y el botón "..."
  // quedaba fuera del scroll horizontal en la PWA — pedido explícito del
  // usuario. En mobile se reemplaza por tarjetas apiladas con el menú de
  // acciones siempre visible.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Listado + paginación
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [search, setSearch] = useState('');
  // 'true' (activos) por defecto — antes era '' (todos mezclados), lo que
  // mostraba pacientes dados de baja mezclados con activos en el listado
  // normal de recepción sin que nadie lo pidiera. El backend interpreta
  // is_active='' (mandado explícitamente al elegir "Todos") como "sin
  // filtrar" — a diferencia de omitir el parámetro del todo, que ahora
  // también cae al default "solo activos".
  const [activeFilter, setActiveFilter] = useState('true'); // '' | 'true' | 'false'
  // Filtro por fecha de alta y orden por última actividad — pedido
  // explícito del usuario ("filtrar por última creación, o última
  // actualización... un médico se olvidó el nombre del paciente y quiere
  // ver rápido los últimos que atendió o editó su HC"). createdFrom/
  // createdTo son fechas tipo <input type="date"> (string "YYYY-MM-DD");
  // sortByActivity alterna el orden entre apellido (default) y última
  // edición/creación (vía AuditLog en el backend, sin columna nueva).
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  // Filtro por última modificación (Patient.updated_at, fecha+hora+segundos
  // reales en el backend) — pedido explícito del usuario 2026-10-20. Mismo
  // patrón de UI que createdFrom/createdTo: solo fecha, sin pedir hora/
  // segundos exactos a mano (el orden "Última modificación" de abajo ya
  // da esa precisión para ver quién se modificó más reciente).
  const [updatedFrom, setUpdatedFrom] = useState('');
  const [updatedTo, setUpdatedTo] = useState('');
  // "Completaron el link de autoservicio" — pedido explícito del usuario,
  // para poder revisar/priorizar a quiénes recepción todavía les falta
  // insistir con el link de CompletarDatos.js. sortBy pasa a ser un string
  // de 3 estados en vez de 2 booleans separados — más simple que agregar
  // un tercer flag independiente, y evita la combinación sin sentido de
  // "ordenar por actividad Y por self-update" a la vez.
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'activity' | 'self_update'
  const [selfUpdateOnly, setSelfUpdateOnly] = useState(false);
  const [showDateFilters, setShowDateFilters] = useState(false);
  const searchTimer = useRef(null);

  // Dialogs
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openComments, setOpenComments] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientData, setPatientData] = useState(emptyPatient);
  const [formError, setFormError] = useState('');
  const [formFieldErrors, setFormFieldErrors] = useState({});

  // Comments
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  // Export
  const [exporting, setExporting] = useState(false);

  // Row action menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPatient, setMenuPatient] = useState(null);

  const loadPatients = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        ...(search ? { search } : {}),
        // Siempre se manda (incluso '' para "Todos") — omitir la key
        // caería en el default "true" del backend en vez de "sin filtrar".
        is_active: activeFilter,
        // created_from/to son fechas puras (sin hora) — to se manda como
        // fin del día (23:59:59) para que el día elegido quede incluido
        // completo, no solo hasta la medianoche de su inicio.
        ...(createdFrom ? { created_from: `${createdFrom}T00:00:00` } : {}),
        ...(createdTo ? { created_to: `${createdTo}T23:59:59` } : {}),
        ...(updatedFrom ? { updated_from: `${updatedFrom}T00:00:00` } : {}),
        ...(updatedTo ? { updated_to: `${updatedTo}T23:59:59` } : {}),
        ...(sortBy === 'activity' ? { order_by: 'last_updated' } : {}),
        ...(sortBy === 'self_update' ? { order_by: 'self_update_completed' } : {}),
        ...(selfUpdateOnly ? { self_update_only: true } : {}),
      };
      const res = await getPatients(params);
      setPatients(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      setPatients([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, activeFilter, createdFrom, createdTo, updatedFrom, updatedTo, sortBy, selfUpdateOnly]);

  useEffect(() => { loadPatients(); }, [loadPatients]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      setSearch(val);
    }, 350);
  };

  const handleActiveFilter = (_, val) => {
    setPage(0);
    setActiveFilter(val ?? '');
  };

  const handleCreate = async () => {
    if (!patientData.first_name.trim() || !patientData.last_name.trim()) {
      setFormError('Nombre y apellido son obligatorios');
      setFormFieldErrors({
        ...(patientData.first_name.trim() ? {} : { first_name: 'Obligatorio' }),
        ...(patientData.last_name.trim() ? {} : { last_name: 'Obligatorio' }),
      });
      return;
    }
    setFormError(''); setFormFieldErrors({});
    try {
      await createPatient({ ...patientData, dob: patientData.dob || null });
      setOpenCreate(false);
      setPatientData(emptyPatient);
      setPage(0);
      await loadPatients();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFormFieldErrors(err.fieldErrors || {});
      if (err.response?.status === 409 && detail?.code === 'DUPLICATE_DNI') {
        setFormError(`Ya existe un paciente con ese DNI: ${detail.existing_name} (ID ${detail.existing_id}). Buscalo en la lista para evitar duplicados.`);
      } else {
        setFormError(err.userMessage || err.message);
      }
    }
  };

  const handleEdit = async () => {
    if (!patientData.first_name.trim() || !patientData.last_name.trim()) {
      setFormError('Nombre y apellido son obligatorios');
      setFormFieldErrors({
        ...(patientData.first_name.trim() ? {} : { first_name: 'Obligatorio' }),
        ...(patientData.last_name.trim() ? {} : { last_name: 'Obligatorio' }),
      });
      return;
    }
    setFormError(''); setFormFieldErrors({});
    try {
      await updatePatient(selectedPatient.id, { ...patientData, dob: patientData.dob || null });
      setOpenEdit(false);
      setPatientData(emptyPatient);
      await loadPatients();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFormFieldErrors(err.fieldErrors || {});
      if (err.response?.status === 409 && detail?.code === 'DUPLICATE_DNI') {
        setFormError(`Ya existe un paciente con ese DNI: ${detail.existing_name} (ID ${detail.existing_id}). Buscalo en la lista para evitar duplicados.`);
      } else {
        setFormError(err.userMessage || err.message);
      }
    }
  };

  const handleOpenEdit = (p) => {
    setSelectedPatient(p);
    setFormError(''); setFormFieldErrors({});
    setPatientData({
      first_name: p.first_name || '', last_name: p.last_name || '', phone: p.phone || '', email: p.email || '',
      address: p.address || '', dob: p.dob ? p.dob.substring(0, 10) : '',
      dni: p.dni || '', gender: p.gender || '', marital_status: p.marital_status || '',
      blood_type: p.blood_type || '', allergies: p.allergies || '',
      chronic_diseases: p.chronic_diseases || '', current_medications: p.current_medications || '',
      insurance_provider: p.insurance_provider || '',
      insurance_policy_number: p.insurance_policy_number || '',
      emergency_contact_name: p.emergency_contact_name || '',
      emergency_contact_phone: p.emergency_contact_phone || '',
      emergency_contact_relationship: p.emergency_contact_relationship || '',
      permanent_notes: p.permanent_notes || '',
      is_active: p.is_active ?? true,
    });
    setOpenEdit(true);
    closeMenu();
  };

  const handleDelete = async (p) => {
    // Es un soft-delete (is_active=False), no un borrado real — el copy
    // refleja eso: pensado para paciente fallecido, que dejó la clínica, o
    // un duplicado a fusionar. No hay una regla de "inactivo por no venir
    // hace tiempo" — eso no se marca acá.
    if (!window.confirm(
      `¿Dar de baja a ${p.name}?\n\nUsalo para paciente fallecido, que dejó la clínica, o un duplicado a fusionar. Podés reactivarlo después desde el filtro "Inactivos".`
    )) return;
    try {
      await apiCall(deletePatient(p.id), 'Error al dar de baja al paciente');
      await loadPatients();
    } catch {}
    closeMenu();
  };

  const handleReactivate = async (p) => {
    try {
      await apiCall(updatePatient(p.id, { is_active: true }), 'Error al reactivar el paciente');
      await loadPatients();
    } catch {}
    closeMenu();
  };

  const handleOpenComments = async (p) => {
    setSelectedPatient(p);
    setNewComment('');
    try { const res = await getComments(p.id); setComments(res.data); }
    catch { setComments([]); }
    setOpenComments(true);
    closeMenu();
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await apiCall(createComment(selectedPatient.id, { comment_text: newComment }), 'Error al agregar nota');
      setNewComment('');
      const res = await getComments(selectedPatient.id);
      setComments(res.data);
    } catch {}
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        ...(search ? { search } : {}),
        ...(activeFilter !== '' ? { is_active: activeFilter } : {}),
      };
      const res = await apiCall(exportPatients(params), 'Error al exportar pacientes');
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Pacientes_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {} finally { setExporting(false); }
  };

  const openMenu = (e, p) => { setMenuAnchor(e.currentTarget); setMenuPatient(p); };
  const closeMenu = () => { setMenuAnchor(null); setMenuPatient(null); };

  const {
    canCreateAppointment, showForm: showNewApptForm, openForm: openNewApptForm,
    closeForm: closeNewApptForm, handleContinue: handleContinueNewAppt,
    canContinue: canContinueNewAppt, FormFields: NewAppointmentFormFields,
    AppointmentDialogNode: newApptDialogNode,
  } = useNewAppointmentFlow({
    patient: newApptPatient || {},
    doctorsArr,
    onSaved: () => setNewApptPatient(null),
  });

  return (
    <Box>
      <PageHeader
        title="Pacientes"
        subtitle={total > 0 ? `${total} paciente${total !== 1 ? 's' : ''} en total` : undefined}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* En mobile "Exportar" pasa a ícono solo — el texto lado a
                lado con "Nuevo paciente" no entraba en una fila sin
                partirse en dos líneas de botones grandes. */}
            {isMobile ? (
              <Tooltip title="Exportar">
                <span>
                  <IconButton onClick={handleExport} disabled={exporting} sx={{ border: 1, borderColor: 'divider' }}>
                    {exporting ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={exporting}
              >
                Exportar
              </Button>
            )}
            {canEdit && features?.patient_self_update && (
              isMobile ? (
                <Tooltip title="Crear paciente por link">
                  <IconButton onClick={() => setSelfUpdateDialog({ patientId: null, patientPhone: null })} sx={{ border: 1, borderColor: 'divider' }}>
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => setSelfUpdateDialog({ patientId: null, patientPhone: null })}
                >
                  Crear paciente por link
                </Button>
              )
            )}
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setPatientData(emptyPatient); setFormError(''); setFormFieldErrors({}); setOpenCreate(true); }}
              >
                Nuevo paciente
              </Button>
            )}
          </Box>
        }
      />

      {/* Barra de filtros — en mobile el buscador y los toggles van cada
          uno en su fila propia a ancho completo, en vez de compartir una
          Paper con padding de desktop que los apretaba a los dos. */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
        <TextField
          placeholder="Buscar por nombre, DNI, teléfono o email…"
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
          }}
          sx={{ flex: 1, minWidth: { sm: 240 } }}
        />
        <ToggleButtonGroup
          value={activeFilter}
          exclusive
          onChange={handleActiveFilter}
          size="small"
          fullWidth={isMobile}
        >
          <ToggleButton value="">Todos</ToggleButton>
          <ToggleButton value="true">Activos</ToggleButton>
          <ToggleButton value="false">Inactivos</ToggleButton>
        </ToggleButtonGroup>
        {/* Más filtros (fecha de alta, orden por última actividad) quedan
            colapsados por default — pedido explícito del usuario, pero de
            uso ocasional (un médico que se olvidó el nombre de alguien
            reciente), no algo que se toque en cada visita a la lista. El
            Chip avisa cuántos filtros de esta sección están activos para
            no tener que abrirla a chequear. */}
        <Button
          size="small" variant={showDateFilters ? 'contained' : 'outlined'}
          startIcon={<FilterListIcon fontSize="small" />}
          onClick={() => setShowDateFilters(p => !p)}
          sx={{ flexShrink: 0 }}
        >
          Más filtros
          {(createdFrom || createdTo || updatedFrom || updatedTo || sortBy !== 'name' || selfUpdateOnly) && (
            <Chip size="small" label={[createdFrom || createdTo, updatedFrom || updatedTo, sortBy !== 'name', selfUpdateOnly].filter(Boolean).length}
              sx={{ ml: 0.75, height: 18, fontSize: '0.65rem' }} />
          )}
        </Button>
      </Paper>

      <Collapse in={showDateFilters}>
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap' }}>
          <TextField
            label="Dado de alta desde" type="date" size="small" value={createdFrom}
            onChange={(e) => { setPage(0); setCreatedFrom(e.target.value); }}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: { sm: 170 } }}
          />
          <TextField
            label="Dado de alta hasta" type="date" size="small" value={createdTo}
            onChange={(e) => { setPage(0); setCreatedTo(e.target.value); }}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: { sm: 170 } }}
          />
          <TextField
            label="Modificado desde" type="date" size="small" value={updatedFrom}
            onChange={(e) => { setPage(0); setUpdatedFrom(e.target.value); }}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: { sm: 170 } }}
          />
          <TextField
            label="Modificado hasta" type="date" size="small" value={updatedTo}
            onChange={(e) => { setPage(0); setUpdatedTo(e.target.value); }}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: { sm: 170 } }}
          />
          <ToggleButtonGroup
            value={sortBy}
            exclusive
            onChange={(_, val) => { if (val) { setPage(0); setSortBy(val); } }}
            size="small"
            fullWidth={isMobile}
          >
            <ToggleButton value="name">Orden: Apellido</ToggleButton>
            <ToggleButton value="activity">Orden: Última modificación</ToggleButton>
            {features?.patient_self_update && (
              <ToggleButton value="self_update">Orden: Completaron datos</ToggleButton>
            )}
          </ToggleButtonGroup>
          {features?.patient_self_update && (
            <ToggleButtonGroup
              value={selfUpdateOnly ? 'yes' : null}
              exclusive
              onChange={(_, val) => { setPage(0); setSelfUpdateOnly(val === 'yes'); }}
              size="small"
              fullWidth={isMobile}
            >
              <ToggleButton value="yes">Solo completaron el link</ToggleButton>
            </ToggleButtonGroup>
          )}
          {(createdFrom || createdTo || updatedFrom || updatedTo || sortBy !== 'name' || selfUpdateOnly) && (
            <Button size="small" onClick={() => {
              setPage(0); setCreatedFrom(''); setCreatedTo(''); setUpdatedFrom(''); setUpdatedTo('');
              setSortBy('name'); setSelfUpdateOnly(false);
            }}>
              Limpiar
            </Button>
          )}
        </Paper>
      </Collapse>

      {/* Estado vacío / cargando, compartido entre mobile y desktop */}
      {loading ? (
        <Paper sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Paper>
      ) : patients.length === 0 ? (
        <Paper sx={{ py: 8, textAlign: 'center' }}>
          <PeopleOutlineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1, display: 'block', mx: 'auto' }} />
          <Typography color="text.secondary">
            {search ? 'No se encontraron pacientes con ese criterio' : 'No hay pacientes registrados'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        /* Tarjetas apiladas en mobile — la tabla de 7 columnas no entraba en
           pantalla y el botón "..." quedaba fuera del scroll horizontal. */
        <Stack spacing={1.5}>
          {patients.map(p => (
            <Paper key={p.id} sx={{ p: 1.5, cursor: 'pointer' }} onClick={() => navigate(`/patients/${p.id}`)}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                <PatientAvatar patient={p} size={40} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                    {/* Solo se muestra cuando aporta información (paciente
                        dado de baja) — el caso normal es estar activo y no
                        vale la pena un chip que aparece siempre. */}
                    {p.is_active === false && (
                      <Chip label="Inactivo" color="default" size="small" sx={{ height: 20, fontSize: '0.68rem' }} />
                    )}
                  </Box>
                  {p.dni && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>DNI: {p.dni}</Typography>}
                  {p.phone && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{p.phone}</Typography>}
                  {(() => { const ins = primaryInsuranceOf(p); return ins && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {ins.provider}{ins.policy_number && ` · ${ins.policy_number}`}
                      {(p.insurances?.length || 0) > 1 && ` (+${p.insurances.length - 1})`}
                    </Typography>
                  ); })()}
                  {/* HC/Turnos/último médico quedaron solo en la tabla de
                      desktop — pedido explícito del usuario de simplificar
                      la vista mobile, que ya tiene bastante texto apilado
                      por tarjeta. */}
                </Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); openMenu(e, p); }} sx={{ flexShrink: 0 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}
          <TablePagination
            component={Paper}
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Stack>
      ) : (
        /* Tabla en desktop/tablet */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }}></TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>DNI</TableCell>
                <TableCell>Teléfono</TableCell>
                <TableCell>Obra social</TableCell>
                {/* Pedido explícito del usuario (2026-10-07): ver de un
                    vistazo cuántas consultas/turnos tiene cada paciente y
                    quién lo vio por última vez, sin entrar a la ficha. Van
                    para todos los roles (confirmado con el usuario) — no
                    filtra nada que recepción no vea ya en otro lado, es
                    solo un conteo. Solo en desktop (ver versión mobile más
                    abajo) — pedido explícito del usuario de simplificar esa
                    vista, no agregar más líneas de texto por tarjeta. */}
                <TableCell align="center" title="Consultas de Historia Clínica">HC</TableCell>
                <TableCell align="center" title="Turnos totales">Turnos</TableCell>
                {/* Médico de cabecera (asignado a mano, o por default el del
                    primer turno — ver Patient.assigned_doctor_id) con
                    prioridad sobre "último médico que lo vio" (el del turno
                    más reciente) — pedido explícito del usuario 2026-10-13. */}
                <TableCell title="Médico de cabecera, o el del último turno si no tiene uno asignado">Médico</TableCell>
                {/* "Estado" (antes acá) era redundante con el filtro
                    Activos/Inactivos de arriba — casi siempre mostraba
                    solo guiones, porque el caso normal es ver activos.
                    Pedido explícito del usuario: sacarla. El chip
                    "Inactivo" sigue existiendo para cuando se filtra por
                    "Todos" (mezcla ambos) — se movió al lado del nombre,
                    mismo lugar donde ya vivía en la vista mobile. */}
                <TableCell align="right" sx={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patients.map(p => (
                <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/patients/${p.id}`)}>
                  <TableCell>
                    <PatientAvatar patient={p} size={32} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                      {p.is_active === false && (
                        <Chip label="Inactivo" color="default" size="small" sx={{ height: 20, fontSize: '0.68rem' }} />
                      )}
                      {(() => {
                        const cfg = CREATED_VIA_CONFIG[p.created_via];
                        if (!cfg?.Icon) return null;
                        const tooltip = p.created_by_username
                          ? `Creado desde ${cfg.label.toLowerCase()} por ${p.created_by_username}`
                          : p.created_via === 'link' ? 'Creado por el propio paciente (link de autocompletado)' : `Creado desde ${cfg.label.toLowerCase()}`;
                        return (
                          <Tooltip title={tooltip}>
                            <cfg.Icon fontSize="inherit" color={cfg.color} sx={{ fontSize: 16 }} />
                          </Tooltip>
                        );
                      })()}
                    </Box>
                    {p.email && <Typography variant="caption" color="text.secondary">{p.email}</Typography>}
                  </TableCell>
                  <TableCell>{p.dni || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                  <TableCell>{p.phone || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                  <TableCell>
                    {(() => { const ins = primaryInsuranceOf(p); return ins
                      ? <Box>
                          <Typography variant="body2">
                            {ins.provider}{(p.insurances?.length || 0) > 1 && ` (+${p.insurances.length - 1})`}
                          </Typography>
                          {ins.policy_number && <Typography variant="caption" color="text.secondary">{ins.policy_number}</Typography>}
                        </Box>
                      : <Typography variant="caption" color="text.disabled">—</Typography>; })()}
                  </TableCell>
                  <TableCell align="center">
                    {p.records_count > 0
                      ? <Chip label={p.records_count} size="small" variant="outlined" sx={{ minWidth: 32 }} />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    {p.appointments_count > 0
                      ? <Chip label={p.appointments_count} size="small" variant="outlined" sx={{ minWidth: 32 }} />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    {p.assigned_doctor_name || p.last_doctor_name || <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Acciones">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openMenu(e, p); }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </TableContainer>
      )}

      {/* Menú de acciones por fila */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 160 } }}>
        <MenuItem dense onClick={() => { closeMenu(); navigate(`/patients/${menuPatient?.id}`); }}>
          Ver perfil completo
        </MenuItem>
        <Divider />
        {canEdit && features?.patient_self_update && (
          <MenuItem dense onClick={() => { setSelfUpdateDialog({ patientId: menuPatient?.id, patientPhone: menuPatient?.phone }); closeMenu(); }}>
            Link para actualizar datos
          </MenuItem>
        )}
        <MenuItem dense onClick={() => { handleOpenComments(menuPatient); }}>
          Notas / comentarios
        </MenuItem>
        {canCreateAppointment && (
          <MenuItem dense onClick={() => { setNewApptPatient(menuPatient); openNewApptForm(); closeMenu(); }}>
            Nuevo turno
          </MenuItem>
        )}
        {canEdit && [
          <MenuItem dense key="edit" onClick={() => handleOpenEdit(menuPatient)}>Editar</MenuItem>,
          <Divider key="div" />,
          menuPatient?.is_active === false ? (
            <MenuItem dense key="reactivate" sx={{ color: 'success.main' }} onClick={() => handleReactivate(menuPatient)}>
              Reactivar
            </MenuItem>
          ) : (
            <MenuItem dense key="del" sx={{ color: 'error.main' }} onClick={() => handleDelete(menuPatient)}>
              Dar de baja
            </MenuItem>
          ),
        ]}
      </Menu>

      {/* Dialog crear */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle>Nuevo paciente</DialogTitle>
        <DialogContent sx={{ overflowY: 'auto' }}>
          <PatientForm patientData={patientData} setPatientData={setPatientData} error={formError} fieldErrors={formFieldErrors} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancelar</Button>
          <Button variant="contained" disabled={!patientData.first_name.trim() || !patientData.last_name.trim()} onClick={handleCreate}>Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog editar */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle>Editar — {selectedPatient?.name}</DialogTitle>
        <DialogContent sx={{ overflowY: 'auto' }}>
          <PatientForm patientData={patientData} setPatientData={setPatientData} error={formError} fieldErrors={formFieldErrors} isEdit />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
          <Button variant="contained" disabled={!patientData.first_name.trim() || !patientData.last_name.trim()} onClick={handleEdit}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog notas */}
      <Dialog open={openComments} onClose={() => setOpenComments(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notas — {selectedPatient?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField fullWidth multiline rows={3} label="Nueva nota" value={newComment}
              onChange={e => setNewComment(e.target.value)} />
            <Button variant="contained" size="small" sx={{ mt: 1 }}
              disabled={!newComment.trim()} onClick={handleAddComment}>
              Agregar
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {comments.length === 0
            ? <Typography color="text.secondary" variant="body2">Sin notas aún.</Typography>
            : comments.map(c => (
                <Box key={c.id} sx={{ p: 1.5, mb: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                  <Typography variant="caption" color="text.secondary">
                    {/* created_at viene naive-UTC del backend — hay que agregar
                        la 'Z' o el navegador lo interpreta como hora local y
                        se muestra ~3hs adelantado en Argentina. */}
                    {new Date(c.created_at.endsWith('Z') ? c.created_at : c.created_at + 'Z').toLocaleString('es-AR')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{c.comment_text}</Typography>
                </Box>
              ))
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenComments(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <SelfUpdateLinkDialog
        open={!!selfUpdateDialog}
        onClose={() => setSelfUpdateDialog(null)}
        patientId={selfUpdateDialog?.patientId}
        patientPhone={selfUpdateDialog?.patientPhone}
      />

      <Dialog open={showNewApptForm} onClose={() => { closeNewApptForm(); setNewApptPatient(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Nuevo turno — {newApptPatient?.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <NewAppointmentFormFields />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { closeNewApptForm(); setNewApptPatient(null); }}>Cancelar</Button>
          <Button variant="contained" disabled={!canContinueNewAppt} onClick={handleContinueNewAppt}>
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
      {newApptDialogNode}
    </Box>
  );
};

export default Patients;
